import axios from "axios";
import config from "./config";
import { mediaKind } from "./mediaLimits";

/**
 * Post / comment image uploads.
 *
 * These used to ride along with the post itself: the composer put the raw files
 * in the `images[]` field of membership-post-create, and the membership API
 * moved them into its own storage and handed back a /storage/posts/... URL. That
 * write is what fails in production, so posting a photo silently produced a post
 * with no image.
 *
 * Now the files go straight to the shared media service and the post request
 * carries the resulting URLs — images in `image_urls[]` (the field GIF picks
 * have always used), video in `video_url`, audio in `audio_url`.
 *
 * The service takes all of them — images, video, audio and (since its August
 * update) documents. It decides the kind from the file's MIME type, not from the
 * field name, and files them under frontend_image/, frontend_video/,
 * frontend_audio/ or frontend_doc/. A File from an <input> always carries its
 * real type, so nothing has to be declared here.
 */

/**
 * The multipart field each kind rides in.
 *
 * Only images are a LIST — a post can carry several, so they go up together in
 * `images[]`. Video, audio and documents are one per post, so the service takes
 * them in singular fields, and a bracketed name it does not know answers
 * `{"error":1,"msg":"No file provided."}` with a 422.
 *
 * The service still decides the KIND from the file's MIME type — the field only
 * says how many to expect — and files each under frontend_image/,
 * frontend_video/, frontend_audio/ or frontend_doc/.
 *
 * Documents are accepted as of the service's August update: pdf, doc, docx,
 * txt, csv, ppt, pptx, xls, xlsx, zip, rar.
 */
const FIELD_BY_KIND = {
  image: "images[]",
  video: "video",
  audio: "audio",
  file: "file",
};

/**
 * How long to let an upload run, from how much there is to send.
 *
 * axiosSetup sets a GLOBAL `axios.defaults.timeout` of 25s so a stalled read
 * can't leave a dead spinner. That number is right for a JSON call and wrong
 * for an upload: 25s is only enough for 43 MB if the member has ~14 Mbps of
 * sustained UPSTREAM bandwidth. A 50 MB video therefore aborted mid-send every
 * time ("Could not post video"), and audio around 5 MB sat right on the edge —
 * which is exactly why it posted on the second or third try and not the first.
 *
 * So budget by size instead, against a deliberately pessimistic 500 kbps floor
 * (~16s per MB) plus 30s of handshake and server-side filing. Still bounded —
 * a genuinely dead connection ends in an error rather than a spinner — but it
 * no longer calls a slow upload a failed one.
 */
export function uploadTimeoutFor(bytes) {
  const mb = Math.max(0, Number(bytes) || 0) / (1024 * 1024);
  return Math.round(30000 + mb * 16000);
}

/** POST one batch that shares a field, and return the URLs it answers with. */
async function postBatch(files, field, { communityToken, memberId }) {
  const fd = new FormData();
  // Both are required by the service — it files uploads under the community and
  // names them after the member.
  fd.append("community_token", communityToken || "");
  fd.append("community_member_id", memberId ?? "");
  files.forEach((f) => fd.append(field, f));

  let res;
  try {
    res = await axios.post(`${config.MEDIA_API_URL}/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: uploadTimeoutFor(files.reduce((n, f) => n + (f?.size || 0), 0)),
    });
  } catch (err) {
    // A timeout and a dropped connection both arrive as ECONNABORTED with no
    // response. Say which it was, because "try again" is good advice for one
    // and useless for the other.
    if (err?.code === "ECONNABORTED" || err?.code === "ETIMEDOUT") {
      throw new Error("The upload timed out. Check your connection and try again.");
    }
    throw new Error(err?.response?.data?.msg || "Could not upload the file.");
  }

  const data = res.data || {};
  if (data.error) {
    throw new Error(data.msg || "Could not upload the file.");
  }
  const urls = Array.isArray(data.urls) ? data.urls : data.url ? [data.url] : [];
  // A short list would silently drop someone's file from their own post, so
  // treat it as a failure rather than posting part of what they attached.
  if (urls.length !== files.length) {
    throw new Error("Some files could not be uploaded. Please try again.");
  }
  return urls;
}

/**
 * Upload files and return their URLs, in the order given.
 *
 * Images travel together in one request; everything else goes one request per
 * file, because its field holds a single file. Mixed lists are handled by
 * splitting them, and the returned array still matches the order passed in.
 *
 * Throws with a message fit to show the member.
 */
export async function uploadFiles(files, { communityToken, memberId }) {
  const list = Array.from(files || []);
  if (list.length === 0) return [];

  const opts = { communityToken, memberId };
  const urls = new Array(list.length);

  const images = [];
  const singles = [];
  list.forEach((file, index) => {
    (mediaKind(file) === "image" ? images : singles).push({ file, index });
  });

  await Promise.all([
    images.length
      ? postBatch(images.map((i) => i.file), FIELD_BY_KIND.image, opts).then((batch) => {
          images.forEach((entry, n) => {
            urls[entry.index] = batch[n];
          });
        })
      : null,
    ...singles.map((entry) =>
      postBatch([entry.file], FIELD_BY_KIND[mediaKind(entry.file)] || FIELD_BY_KIND.file, opts).then(
        ([url]) => {
          urls[entry.index] = url;
        }
      )
    ),
  ].filter(Boolean));

  return urls;
}

/** Upload one file (image, video, audio or document) and return its URL. */
export async function uploadFile(file, opts) {
  const [url] = await uploadFiles([file], opts);
  return url;
}

/**
 * Turn a composer media list into the two URL lists the post/comment endpoints
 * take, uploading any newly picked files on the way.
 *
 * Entries are `{ file }` (just picked), `{ isGif, url }` (GIPHY/Tenor) or
 * `{ isExisting, url }` (already on the post, editor only).
 *
 * Returns `{ keep, fresh }` — `keep` goes to keep_images[] (what the editor still
 * shows of the original set) and `fresh` to image_urls[]. Order within `fresh`
 * follows the composer, so the post shows the images the way they were arranged.
 */
export async function resolveComposerImages(media, opts) {
  const list = media || [];
  const files = list.filter((m) => m.file).map((m) => m.file);
  const uploaded = await uploadFiles(files, opts);

  const keep = [];
  const fresh = [];
  let next = 0;
  list.forEach((m) => {
    if (m.isExisting) keep.push(m.url);
    else if (m.file) fresh.push(uploaded[next++]);
    else if (m.url) fresh.push(m.url);
  });
  return { keep, fresh };
}
